import type { RuleOutcomeDto } from '@signalvote/shared';
import { getGates, getNotifications, getNoticeFlags, getWarningFlags, isHeadsUp } from '@signalvote/shared';

export interface ComplianceOutcomesViewProps {
  outcomes: RuleOutcomeDto[];
}

// One rule can fire more than once, such as one heads-up per later gift it names.
const outcomeKey = (o: RuleOutcomeDto, index: number) =>
  `${o.ruleCode}-${o.metadata?.laterContributionId ?? o.metadata?.laterLoanId ?? index}`;

export function ComplianceOutcomesView({ outcomes }: ComplianceOutcomesViewProps) {
  const gates = getGates(outcomes);
  const notifications = getNotifications(outcomes);
  // Flags never block the save, but a treasurer who can't see one at entry can't act on it.
  const headsUps = [...notifications.filter(isHeadsUp), ...getWarningFlags(outcomes)];
  const filingObligations = [...notifications.filter((n) => !isHeadsUp(n)), ...getNoticeFlags(outcomes)];

  if (gates.length === 0 && headsUps.length === 0 && filingObligations.length === 0) return null;

  return (
    <>
      {gates.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          {gates.map((g, i) => (
            <p key={outcomeKey(g, i)} className="text-sm text-red-800">
              {g.message}
            </p>
          ))}
        </div>
      )}

      {headsUps.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-800 mb-1">Heads up</p>
          {headsUps.map((h, i) => (
            <p key={outcomeKey(h, i)} className="text-sm text-amber-800">
              {h.message}
            </p>
          ))}
        </div>
      )}

      {filingObligations.length > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm font-semibold text-blue-800 mb-1">Filing Obligations</p>
          {filingObligations.map((n, i) => (
            <p key={outcomeKey(n, i)} className="text-sm text-blue-800">
              {n.message}
              {n.metadata?.windowBusinessDays && (
                <span className="ml-1 text-xs text-blue-600">
                  (File within {n.metadata.windowBusinessDays} business days)
                </span>
              )}
            </p>
          ))}
        </div>
      )}
    </>
  );
}
